/**
 * Normalization side panel for one selected space: occupancy override
 * dropdown, exit toggle, exclusion tier, ambient dBA. Every change calls
 * `onPatch` immediately (the store applies it optimistically and PATCHes).
 */
import { useEffect, useState } from "react";
import type { CSSProperties, ChangeEvent } from "react";
import {
  EXCLUSION_TIERS,
  type ApiSpace,
  type ExclusionTier,
  type SpacePatch,
} from "../api/types";
import { useIstariStore } from "../state/store";

const fieldStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "0.25rem",
  marginBottom: "0.7rem",
};

const labelStyle: CSSProperties = {
  fontSize: "0.72rem",
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  color: "#8e8eb8",
};

const inputStyle: CSSProperties = {
  background: "#2a2a44",
  color: "#d8d8e8",
  border: "1px solid #44446a",
  borderRadius: 6,
  padding: "0.35rem 0.45rem",
  fontSize: "0.85rem",
};

const EXCLUSION_LABELS: Record<ExclusionTier, string> = {
  none: "None",
  exclusion: "Exclusion (soft avoid)",
  no_go: "No-go (hard block)",
};

const badgeBaseStyle: CSSProperties = {
  display: "inline-block",
  alignSelf: "flex-start",
  fontSize: "0.68rem",
  fontWeight: 600,
  letterSpacing: "0.02em",
  padding: "0.1rem 0.4rem",
  borderRadius: 999,
  border: "1px solid transparent",
  cursor: "help",
};

/**
 * Read-only provenance badge for the effective occupancy (T1). Advisory only.
 * `confirmed` is the only "trusted" style; inferred / struct_gap_fill are
 * deliberately neutral ("verify"), and conflicting is a warning. A confirmed
 * source attests only to the coarse category, never sub-category ventilation
 * adequacy — so the trusted style stays subtle.
 */
type OccupancySource = NonNullable<ApiSpace["occupancy_source"]>;

const OCCUPANCY_SOURCE_BADGE: Record<
  OccupancySource,
  { label: string; title: string; style: CSSProperties }
> = {
  confirmed: {
    label: "Confirmed",
    title: "Attests to the coarse category, not sub-category ventilation adequacy.",
    style: {
      background: "#183a2a",
      color: "#7fd8a6",
      borderColor: "#2f6b4c",
    },
  },
  inferred: {
    label: "Unconfirmed — verify",
    title: "Occupancy inferred from the room name — please verify.",
    style: {
      background: "#2a2a44",
      color: "#a8a8c8",
      borderColor: "#44446a",
    },
  },
  struct_gap_fill: {
    label: "Unconfirmed — verify",
    title: "Occupancy from IFC data only — no room-name corroboration.",
    style: {
      background: "#2a2a44",
      color: "#a8a8c8",
      borderColor: "#44446a",
    },
  },
  conflicting: {
    label: "Sources disagree — verify",
    title:
      "The room name and the IFC classification disagreed — please verify the occupancy.",
    style: {
      background: "#3a2a18",
      color: "#e8b878",
      borderColor: "#6b4c2f",
    },
  },
};

export interface SpacePanelProps {
  space: ApiSpace;
  onPatch: (patch: SpacePatch) => void;
  /**
   * Active ruleset for the secondary subtype picker (ENG-14678 / ADR 0011).
   * When set and the chosen occupancy has subtypes for this ruleset, a
   * secondary "Refine" dropdown appears. When `undefined`, the secondary
   * picker is hidden.
   */
  rulesetId?: string;
}

export function SpacePanel({ space, onPatch, rulesetId }: SpacePanelProps) {
  // Occupancy dropdown options come from the canonical vocab fetched on wizard
  // mount (Normalize.tsx) and cached in the store. See ADR 0010.
  const occupancyVocab = useIstariStore((s) => s.occupancyVocab);
  // Per-ruleset vocab embeds subtypes so we can render the secondary picker.
  const rulesetVocab = useIstariStore((s) =>
    rulesetId ? (s.occupancyVocabByRuleset[rulesetId] ?? null) : null,
  );

  const effectiveOccupancy = space.occupancy_override ?? space.occupancy ?? "";
  const subtypesForOccupancy =
    rulesetVocab?.find((e) => e.key === effectiveOccupancy)?.subtypes ?? [];
  const currentSubtype = rulesetId ? (space.subtypes[rulesetId] ?? "") : "";

  // Ambient dBA edits locally, commits on blur/Enter — a PATCH per keystroke
  // would be noisy and makes partial numbers ("4") race the server echo.
  const [dbaText, setDbaText] = useState(
    space.ambient_dba !== null ? String(space.ambient_dba) : "",
  );
  useEffect(() => {
    setDbaText(space.ambient_dba !== null ? String(space.ambient_dba) : "");
  }, [space.id, space.ambient_dba]);

  const commitDba = () => {
    if (dbaText.trim() === "") {
      if (space.ambient_dba !== null) onPatch({ ambient_dba: null });
      return;
    }
    const value = Number(dbaText);
    if (Number.isFinite(value) && value !== space.ambient_dba) {
      onPatch({ ambient_dba: value });
    }
  };

  const onOccupancyChange = (e: ChangeEvent<HTMLSelectElement>) => {
    onPatch({
      occupancy_override: e.target.value === "" ? null : e.target.value,
    });
  };

  return (
    <div data-testid="space-panel">
      <h3 style={{ margin: "0 0 0.2rem", fontSize: "1rem" }}>{space.name}</h3>
      <div
        style={{
          color: "#8e8eb8",
          fontSize: "0.8rem",
          marginBottom: "0.8rem",
        }}
      >
        {space.storey_name ?? "No storey"}
        {space.area_m2 !== null && ` · ${space.area_m2.toFixed(1)} m²`}
        {space.occupancy !== null &&
          ` · classified: ${space.occupancy}${space.occupancy_override !== null ? " (overridden)" : ""}`}
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle} htmlFor="space-occupancy">
          Occupancy override
        </label>
        {space.occupancy_source != null && (
          <span
            data-testid="occupancy-source-badge"
            title={OCCUPANCY_SOURCE_BADGE[space.occupancy_source].title}
            style={{
              ...badgeBaseStyle,
              ...OCCUPANCY_SOURCE_BADGE[space.occupancy_source].style,
            }}
          >
            {OCCUPANCY_SOURCE_BADGE[space.occupancy_source].label}
          </span>
        )}
        <select
          id="space-occupancy"
          data-testid="space-occupancy"
          style={inputStyle}
          value={space.occupancy_override ?? ""}
          onChange={onOccupancyChange}
        >
          <option value="">— use classification —</option>
          {occupancyVocab === null ? (
            <option disabled>Loading…</option>
          ) : (
            occupancyVocab.map((entry) => (
              <option key={entry.key} value={entry.key}>
                {entry.label}
              </option>
            ))
          )}
        </select>
      </div>

      {rulesetId && subtypesForOccupancy.length > 0 && (
        <div style={fieldStyle}>
          <label style={labelStyle} htmlFor="space-subtype">
            Refine for {rulesetId}
          </label>
          <select
            id="space-subtype"
            data-testid="space-subtype"
            style={inputStyle}
            value={currentSubtype}
            onChange={(e) => {
              const next = { ...space.subtypes };
              if (e.target.value === "") delete next[rulesetId];
              else next[rulesetId] = e.target.value;
              onPatch({ subtypes: next });
            }}
          >
            <option value="">— none —</option>
            {subtypesForOccupancy.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <div style={{ ...fieldStyle, flexDirection: "row", gap: "0.5rem" }}>
        <input
          id="space-is-exit"
          data-testid="space-is-exit"
          type="checkbox"
          checked={space.is_exit}
          onChange={(e) => onPatch({ is_exit: e.target.checked })}
        />
        <label htmlFor="space-is-exit" style={{ fontSize: "0.85rem" }}>
          Exit (egress point)
        </label>
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle} htmlFor="space-exclusion">
          Exclusion tier
        </label>
        <select
          id="space-exclusion"
          data-testid="space-exclusion"
          style={inputStyle}
          value={space.exclusion}
          onChange={(e) => onPatch({ exclusion: e.target.value as ExclusionTier })}
        >
          {EXCLUSION_TIERS.map((tier) => (
            <option key={tier} value={tier}>
              {EXCLUSION_LABELS[tier]}
            </option>
          ))}
        </select>
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle} htmlFor="space-ambient-dba">
          Ambient sound (dBA)
        </label>
        <input
          id="space-ambient-dba"
          data-testid="space-ambient-dba"
          type="number"
          inputMode="decimal"
          style={inputStyle}
          placeholder="default"
          value={dbaText}
          onChange={(e) => setDbaText(e.target.value)}
          onBlur={commitDba}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitDba();
          }}
        />
      </div>
    </div>
  );
}
